import {AxiosResponse} from "axios";
import {apiClient, createRetryingNotificationHandler, RetryConfig} from "./apiClient";
import {IStandardPagedResponse, IStandardResponse} from "../types";
import {IApiToken, IApiTokenCreateResponse, TApiTokenId} from "../types/IApiToken";
import {IApiTokenCreateResponseDTO, IApiTokenDTO} from "../types/dtos/IApiTokenDTO";

export interface ICreateApiTokenRequest {
    name: string;
    expires_in_days: number;
}

export interface IRenewApiTokenRequest {
    expires_in_days: number;
}

const fromApiTokenDTO = (dto: IApiTokenDTO): IApiToken => {
    return {
        id: dto.id,
        name: dto.name,
        lastUsedAt: dto.last_used_at ? new Date(dto.last_used_at) : null,
        expiresAt: new Date(dto.expires_at),
        createdAt: new Date(dto.created_at),
    };
};

const fromApiTokenCreateResponseDTO = (dto: IApiTokenCreateResponseDTO): IApiTokenCreateResponse => {
    return {
        token: dto.token,
        id: dto.id,
        name: dto.name,
        expiresAt: new Date(dto.expires_at),
        createdAt: new Date(dto.created_at),
    };
};

export const listApiTokens = async (companyId: string): Promise<Array<IApiToken>> => {
    const url = `/companies/${companyId}/tokens`;
    const response: AxiosResponse<IStandardPagedResponse<IApiTokenDTO>> = await apiClient.get(url, {
        retry: true,
        maxRetries: 100,
        backoff: 300,
        onRetry: createRetryingNotificationHandler({message: "Loading API Tokens Failed."}),
    } as RetryConfig);

    return response.data.data.map(fromApiTokenDTO);
};

export const createApiToken = async (
    companyId: string,
    request: ICreateApiTokenRequest
): Promise<IApiTokenCreateResponse> => {
    const url = `/companies/${companyId}/tokens`;
    const response: AxiosResponse<IStandardResponse<IApiTokenCreateResponseDTO>> = await apiClient.post(
        url,
        request,
        {
            retry: false,
        } as RetryConfig
    );

    return fromApiTokenCreateResponseDTO(response.data.data);
};

export const renewApiToken = async (
    companyId: string,
    tokenId: TApiTokenId,
    request: IRenewApiTokenRequest
): Promise<IApiToken> => {
    const url = `/companies/${companyId}/tokens/${tokenId}/renew`;
    const response: AxiosResponse<IStandardResponse<IApiTokenDTO>> = await apiClient.post(
        url,
        request,
        {
            retry: false,
        } as RetryConfig
    );

    return fromApiTokenDTO(response.data.data);
};

export const deleteApiToken = async (companyId: string, tokenId: TApiTokenId): Promise<void> => {
    const url = `/companies/${companyId}/tokens/${tokenId}`;
    await apiClient.delete(url, {
        retry: false,
    } as RetryConfig);
};

